<template>
  <BaseDialog
    v-model="internalValue"
    @input="$emit('input', $event)"
    :title="'Crear Esdeveniment'"
    :subtitle="'Introdueix les dades del nou esdeveniment'"
    :cancelText="'Cancel·la'"
    :confirmText="'Crear'"
    :loading="loading"
    :maxWidth="'600px'"
    :backgroundColor="'#e0f5e9'"
    :confirmColor="'#4caf50'"
    role="dialog"
    aria-modal="true"
    @cancel="onCancel"
    @confirm="onConfirm"
  >
    <v-form ref="form">
      <!-- Event Type Selector -->
      <v-select
        v-model="newEvent.type"
        :items="eventTypeOptions"
        item-value="value"
        item-text="text"
        :label="'Tipus d’esdeveniment'"
        :rules="requiredRule"
        @change="resetConditionalFields"
      />

      <!-- Time -->
      <v-text-field
        v-model="newEvent.data.time"
        type="number"
        min="0"
        :label="'Minut'"
        :rules="requiredRule"
      />

      <!-- Team -->
      <v-select
        v-model="newEvent.data.team"
        :items="teamOptions"
        item-value="id"
        item-text="name"
        :label="'Equip'"
        :rules="requiredRule"
      >
        <template slot="selection" slot-scope="{ item }">
          <v-avatar size="32" class="mr-2" v-if="item && item.shield">
            <img :src="item.shield" :alt="item.name" />
          </v-avatar>
          {{ item && item.name ? item.name : '' }}
        </template>

        <template slot="item" slot-scope="{ item }">
          <v-list-tile-avatar v-if="item.shield">
            <img :src="item.shield" :alt="item.name" />
          </v-list-tile-avatar>
          <v-list-tile-content>
            <v-list-tile-title>{{ item.name }}</v-list-tile-title>
          </v-list-tile-content>
        </template>
      </v-select>

      <!-- Player In -->
      <v-select
        v-if="newEvent.type === 'change'"
        v-model="newEvent.data.player_in"
        :items="availablePlayers"
        item-value="value"
        item-text="name"
        :label="'Jugador Entra'"
        :rules="requiredRule"
      >
        <template slot="selection" slot-scope="{ item }">
          <v-avatar size="24" class="mr-2" style="background-color: #003784">
            <span class="subtitle-2 white--text">{{ item.number }}</span>
          </v-avatar>
          {{ item.name }}
        </template>
        <template slot="item" slot-scope="{ item }">
          <v-list-tile-avatar>
            <v-avatar size="32" style="background-color: #003784">
              <span class="subtitle-2 white--text">{{ item.number }}</span>
            </v-avatar>
          </v-list-tile-avatar>
          <v-list-tile-content>
            <v-list-tile-title>{{ item.name }}</v-list-tile-title>
          </v-list-tile-content>
        </template>
      </v-select>

      <!-- Player Out -->
      <v-select
        v-if="newEvent.type === 'change'"
        v-model="newEvent.data.player_out"
        :items="availablePlayers"
        item-value="value"
        item-text="name"
        :label="'Jugador Surt'"
        :rules="requiredRule"
      >
        <template slot="selection" slot-scope="{ item }">
          <v-avatar size="24" class="mr-2" style="background-color: #003784">
            <span class="subtitle-2 white--text">{{ item.number }}</span>
          </v-avatar>
          {{ item.name }}
        </template>
        <template slot="item" slot-scope="{ item }">
          <v-list-tile-avatar>
            <v-avatar size="32" style="background-color: #003784">
              <span class="subtitle-2 white--text">{{ item.number }}</span>
            </v-avatar>
          </v-list-tile-avatar>
          <v-list-tile-content>
            <v-list-tile-title>{{ item.name }}</v-list-tile-title>
          </v-list-tile-content>
        </template>
      </v-select>

      <!-- Single Player Selector -->
      <v-select
        v-if="config.requiresPlayer"
        v-model="newEvent.data.player_id"
        :items="availablePlayers"
        item-value="value"
        item-text="name"
        :label="'Jugador'"
        :rules="requiredRule"
      >
        <template slot="selection" slot-scope="{ item }">
          <v-avatar size="24" class="mr-2" style="background-color: #003784">
            <span class="subtitle-2 white--text">{{ item.number }}</span>
          </v-avatar>
          {{ item.name }}
        </template>
        <template slot="item" slot-scope="{ item }">
          <v-list-tile-avatar>
            <v-avatar size="32" style="background-color: #003784">
              <span class="subtitle-2 white--text">{{ item.number }}</span>
            </v-avatar>
          </v-list-tile-avatar>
          <v-list-tile-content>
            <v-list-tile-title>{{ item.name }}</v-list-tile-title>
          </v-list-tile-content>
        </template>
      </v-select>

      <!-- Subtype -->
      <v-select
        v-if="config.showSubtype"
        v-model="newEvent.data.subtype"
        :items="config.subtypeOptions"
        item-text="text"
        item-value="value"
        :label="'Subtipo'"
        clearable
        @input="onSubtypeInput"
      />

      <!-- Comment -->
      <v-text-field
        v-if="config.showComment"
        v-model="newEvent.data.comment"
        :label="'Comentari'"
      />

      <!-- Ambulance -->
      <v-switch
        v-if="newEvent.type === 'injury'"
        v-model="newEvent.data.ambulance"
        :label="'Ambulància'"
      />
    </v-form>
  </BaseDialog>
</template>

<script>
import BaseDialog from './BaseDialog.vue'
import i18n from '@/i18n'

export default {
  name: 'CreateEventDialog',
  components: { BaseDialog },
  props: {
    value: { type: Boolean, required: true },
    matchTeams: Object,
    loading: { type: Boolean, default: false }
  },
  data () {
    return {
      requiredRule: [v => !!v || 'Aquest camp és obligatori'],
      redCardSubtypes: [
        { value: '321g', text: '321g: ' + this.$t('punishment.321g') },
        { value: '336', text: '336: ' + this.$t('punishment.336') },
        { value: '337', text: '337: ' + this.$t('punishment.337') },
        { value: '338a', text: '338a: ' + this.$t('punishment.338a') },
        { value: '338c', text: '338c: ' + this.$t('punishment.338c') },
        { value: '338d', text: '338d: ' + this.$t('punishment.338d') },
        { value: '338f', text: '338f: ' + this.$t('punishment.338f') },
        { value: '338.2b', text: '338.2b: ' + this.$t('punishment.3382b') },
        { value: '339', text: '339: ' + this.$t('punishment.339') },
        { value: '355sc', text: '355sc: ' + this.$t('punishment.355sc') }
      ],
      injuryTypes: [
        { value: 'Lleu', text: 'Lleu' },
        { value: 'Mitjana', text: 'Mitjana' },
        { value: 'Greu', text: 'Greu' }
      ],
      newEvent: {
        type: null,
        data: {
          time: 0,
          team: null,
          player_id: null,
          player_in: null,
          player_out: null,
          subtype: '',
          comment: '',
          ambulance: false
        }
      },
      eventConfigs: {
        goal: { requiresPlayer: true },
        autogoal: { requiresPlayer: true },
        change: { requiresPlayer: false, requiresTwoPlayers: true },
        yellowcard: { requiresPlayer: true },
        redcard: { requiresPlayer: true, showSubtype: true, showComment: true, subtypeOptions: [] },
        injury: { requiresPlayer: true, showSubtype: true, showComment: true, showAmbulance: true, subtypeOptions: [] },
        corner: { requiresPlayer: false },
        offside: { requiresPlayer: false }
      }
    }
  },
  created () {
    this.eventConfigs.redcard.subtypeOptions = this.redCardSubtypes
    this.eventConfigs.injury.subtypeOptions = this.injuryTypes
  },
  computed: {
    internalValue: {
      get () { return this.value },
      set (val) { this.$emit('input', val) }
    },
    config () {
      return this.eventConfigs[this.newEvent.type] || {}
    },
    teamOptions () {
      return ['home', 'away'].map(key => {
        const team = this.matchTeams[key] || {}
        return {
          id: key,
          name: team.name || key,
          shield: team.shield || null
        }
      })
    },
    availablePlayers () {
      let teamKey
      if (typeof this.newEvent.data.team === 'string') {
        teamKey = this.newEvent.data.team
      } else if (this.newEvent.data.team && this.newEvent.data.team.id) {
        teamKey = this.newEvent.data.team.id
      }
      const players = (this.matchTeams[teamKey] || {}).players || {}
      return Object.values(players).map(p => ({
        value: p.id,
        number: p.number,
        name: p.name.split(' ')[0].toUpperCase()
      }))
    },
    eventTypeOptions () {
      return Object.keys(this.eventConfigs).map(type => ({
        value: type,
        text: this.getTypeName(type)
      }))
    }
  },
  watch: {
    'newEvent.data.team' () {
      this.newEvent.data.player_id = null
      this.newEvent.data.player_in = null
      this.newEvent.data.player_out = null
    }
  },
  methods: {
    onCancel () {
      if (this.loading) return
      this.$emit('input', false)
      this.resetForm()
    },
    onConfirm () {
      if (!this.$refs.form.validate()) return

      const type = this.newEvent.type
      const time = parseInt(this.newEvent.data.time, 10)
      const team = this.newEvent.data.team
      const date = new Date().getTime()

      if (type === 'change') {
        const desc = this.getDescription()

        const changeInEvent = {
          type: 'changein',
          data: {
            time,
            team,
            player_id: this.newEvent.data.player_in,
            description: desc.description_player_in
          },
          date
        }

        const changeOutEvent = {
          type: 'changeout',
          data: {
            time,
            team,
            player_id: this.newEvent.data.player_out,
            description: desc.description_player_out
          },
          date
        }

        const events = [changeInEvent, changeOutEvent]
        this.$emit('confirm', JSON.stringify(events))
        return
      }

      // For all other types
      const allowedFields = {
        goal: ['time', 'team', 'player_id'],
        autogoal: ['time', 'team', 'player_id'],
        yellowcard: ['time', 'team', 'player_id'],
        redcard: ['time', 'team', 'player_id', 'subtype', 'comment'],
        injury: ['time', 'team', 'player_id', 'subtype', 'comment', 'ambulance'],
        corner: ['time', 'team'],
        offside: ['time', 'team']
      }

      const fields = allowedFields[type] || []
      const cleanData = this.filterFields(this.newEvent.data, fields)
      if (cleanData.time) cleanData.time = time
      Object.assign(cleanData, this.getDescription())

      const cleanEvent = {
        type,
        data: cleanData,
        date
      }

      const events = [cleanEvent]
      this.$emit('confirm', JSON.stringify(events))
    },
    resetConditionalFields () {
      this.newEvent.data.subtype = ''
      this.newEvent.data.comment = ''
      this.newEvent.data.ambulance = false
      this.newEvent.data.player_id = null
      this.newEvent.data.player_in = null
      this.newEvent.data.player_out = null
    },
    resetForm () {
      this.newEvent = {
        type: null,
        data: {
          time: 0,
          team: null,
          player_id: null,
          player_in: null,
          player_out: null,
          subtype: '',
          comment: '',
          ambulance: false
        }
      }
      if (this.$refs.form) {
        this.$refs.form.resetValidation()
      }
    },
    onSubtypeInput (value) {
      this.newEvent.data.subtype = value === undefined ? '' : value
    },
    filterFields (source, allowed) {
      return allowed.reduce((obj, key) => {
        if (source[key] !== undefined) obj[key] = source[key]
        return obj
      }, {})
    },
    getPlayerDescription (playerId) {
      const player = this.availablePlayers.find(p => p.value === playerId)
      return player ? `(${player.number}) ${player.name}` : ''
    },
    getDescription () {
      if (this.newEvent.type === 'change') {
        return {
          description_player_in: this.getPlayerDescription(this.newEvent.data.player_in),
          description_player_out: this.getPlayerDescription(this.newEvent.data.player_out)
        }
      }
      if (this.config.requiresPlayer) {
        return {
          description: this.getPlayerDescription(this.newEvent.data.player_id)
        }
      }
      return { description: '' }
    },
    getTypeName (type) {
      const map = {
        uniforms: 'miccamps.status.uniforms',
        ready: 'miccamps.status.players',
        start1: 'miccamps.match_actions.start1',
        stop1: 'miccamps.match_actions.stop1',
        start2: 'miccamps.match_actions.start2',
        stop2: 'miccamps.match_actions.stop2',
        penalties: 'miccamps.status.penalties',
        end: 'miccamps.status.end',
        closed: 'miccamps.status.closed',
        corner: 'miccamps.match_actions.corner',
        offside: 'miccamps.match_actions.offside',
        goal: 'miccamps.match_actions.goal',
        autogoal: 'miccamps.match_actions.autogoal',
        yellowcard: 'miccamps.match_actions.yellowcard',
        redcard: 'miccamps.match_actions.redcard',
        injury: 'miccamps.match_actions.injury',
        penalty: 'miccamps.match_actions.penalty',
        fail: 'miccamps.match_actions.fail',
        success: 'miccamps.match_actions.success',
        change: 'miccamps.match_actions.change',
        comment: 'miccamps.match_actions.comment'
      }
      return i18n.t(map[type] || type)
    }
  }
}
</script>
